/**
 * Welche Scopes bei der Anmeldung angefordert werden.
 *
 * Frei von allem anderen, weil hier ein einziges fehlendes Wort dazu führt,
 * dass sich Leute alle fünf Minuten neu anmelden müssen — und weil man das
 * am laufenden System nur schwer sieht.
 *
 * **Der Zusammenhang.** Ohne `offline_access` gibt Authentik kein
 * Refresh-Token aus. `oidc-client-ts` fällt dann auf die stille Erneuerung
 * im versteckten Rahmen zurück (`prompt=none` im iframe) — und die scheitert
 * an Authentiks eigenem `X-Frame-Options: DENY`. Ergebnis: Nach Ablauf des
 * Zugriffstokens (fünf Minuten) landet man auf der Anmeldeseite. Genau
 * deshalb ist `offline_access` hier nicht optional, sondern wird ergänzt,
 * auch wenn eine Umgebungsvariable es vergisst.
 */

/** Ohne diese drei funktioniert die Anmeldung nicht. */
const PFLICHT = ["openid", "profile", "email"]

/** Ohne dieses gibt es kein Refresh-Token — siehe Kopfkommentar. */
export const OFFLINE = "offline_access"

/**
 * Scope-Zeichenkette aufbauen.
 *
 * `vorgabe` ist der Wert aus der Laufzeitkonfiguration (`OIDC_SCOPE`). Er
 * darf ergänzen, aber nichts Notwendiges weglassen: Eine Umgebung, in der
 * jemand `openid` vergisst, soll nicht stillschweigend kaputtgehen.
 */
export function scopeAufbauen(vorgabe?: string): string {
  const teile = (vorgabe ?? "").split(/\s+/).filter(Boolean)
  const alle = [...PFLICHT, OFFLINE, ...teile]
  // Reihenfolge stabil halten und Doppelungen entfernen — manche
  // Autorisierungsserver spiegeln den Scope in Fehlermeldungen wider, und
  // „openid openid profile" liest sich wie ein Fehler, der es nicht ist.
  return [...new Set(alle)].join(" ")
}

/**
 * Hat die Anmeldung ein Refresh-Token gebracht?
 *
 * Wenn nicht, obwohl `offline_access` angefordert wurde, liegt es fast
 * immer daran, dass der Scope am Provider in Authentik nicht freigegeben
 * ist. Das ist von der Anwendung aus nicht zu beheben — aber es soll
 * lesbar im Protokoll stehen, statt sich als „ich muss mich dauernd neu
 * anmelden" zu äußern.
 */
export function refreshTokenFehlt(user: { refresh_token?: string } | null): boolean {
  return user !== null && !user.refresh_token
}
